// src/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNotifications, NotificationContext } from './NotificationContext';
import { BASE_URI } from './Config';

interface AuthContextProps {
    userId: string | null;
    userToken: string | null;
    userRole: string | null;
    userVerified: Boolean;
    userLineQuota: number | null;
    userEmail: string | null;
    loading: Boolean;
    signIn: (email: string, password: string) => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [userToken, setUserToken] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userVerified, setUserVerified] = useState<Boolean>(false);
    const [userEmail, setUserEmail] = useState<null | string>(null);
    const [userLineQuota, setUserLineQuota] = useState<null | number>(null);
    const [loading, setLoading] = useState(true);
    const notificationContext = useContext(NotificationContext);
    // const { pushMessage } = useNotifications();


    useEffect(() => {
        if (sessionStorage.getItem("access_token")) {
            const timer = setTimeout(() => {
                setLoading(false);
            }, 100); 
        } 
        else {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        console.log("I triggered but token is null?");

        if (userToken) {
            // Assuming the token contains the user role or you fetch the role based on the token.
            console.log("Now checking user role");

            checkUserRole();//.then(() => setLoading(false));
        }
        // setLoading(false);  // Set loading to false after the check
    }, [userToken]);

    useEffect(() => {
        console.log("I triggered");
        const token = sessionStorage.getItem('access_token');
        console.log(token);
        if (token) {
            console.log("I set the token");
            // Set the user based on the token.
            // Ideally, you'd validate the token with your server.
            setUserToken(token);
        } else {
            //setLoading(false);  // Set loading to false after the check
        }
    }, []);

    const checkUserRole = async () => {
        try {
            const response = await fetch(`${BASE_URI}/current_user_role`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                }
            });

            const data = await response.json();

            if (data.user) {
                console.log(data.user);
                setUserRole(data.user.role);
                setUserVerified(data.user.verified);
                setUserEmail(data.user.email);
                setUserLineQuota(data.user.line_quota);
                setUserId(data.user.id);
                // Redirect or perform other actions after successful signup
            }
            else {
                console.log("Retrieving user failed!");
                setUserToken(null);
            }
        } catch (error) {
            console.error("There was an error retrieving user role:", error);
        }
    };

    const signIn = async (email: string, password: string) => {

        const userData = `username=${email}&password=${password}`;

        try {
            const response = await fetch(`${BASE_URI}/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: userData
            });

            const data = await response.json();

            if (data.access_token) {
                console.log("Login successful!");
                sessionStorage.setItem('access_token', data.access_token);
                setUserToken(data.access_token);
                return true;

            } else {
                notificationContext?.pushMessage({text: "Username or password incorrect", type: "error"})
                console.log("Login failed!");
            }
        } catch (error) {
            console.error("There was an error during login:", error);
        }
    };

    const signOut = () => {
        setUserToken(null);
        setUserRole(null);
        sessionStorage.removeItem('access_token');
    };

    return (
        <AuthContext.Provider value={{ userToken, loading, userRole, userVerified, userEmail, userId, userLineQuota, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};